import React, { useMemo, useState } from "react";
import { Card, Select, Table } from "antd";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { ApiManager, Locale } from "../../utils";
import { defaultDateRange, formatMoneyFen } from "../../utils/statistics/statisticsUtils";
import useStatisticsQuery from "./hooks/useStatisticsQuery";
import StatisticsDateFilter from "./components/StatisticsDateFilter";
import StatCardGrid, { buildMoneyCard } from "./components/StatCardGrid";
import TrendChart from "./components/TrendChart";
import PeriodCompare from "./components/PeriodCompare";
import ExportCsvButton from "./components/ExportCsvButton";
import "./statisticsShared.scss";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();

const STATUS_OPTIONS = ["all", "in_sales", "stop_selling"];

function ProductStatistics({ user }) {
  const [dateRange, setDateRange] = useState(defaultDateRange(7));
  const [productStatus, setProductStatus] = useState("all");

  const filters = useMemo(() => {
    if (productStatus === "all") return {};
    return { product_status: productStatus };
  }, [productStatus]);

  const { loading, data } = useStatisticsQuery({
    endpoint: api.statistics.products,
    token: user?.token,
    dateRange,
    filters,
  });

  const catalog = data?.catalog || {};
  const sales = data?.sales_summary || {};
  const created = data?.created_summary || {};
  const edited = data?.edited_summary || {};
  const comparison = data?.comparison || {};
  const topProducts = data?.top_products || [];

  const statCards = [
    { key: "total", title: i18n.t("statisticsOverview.productTotal"), value: catalog.total ?? 0 },
    { key: "inSales", title: i18n.t("statisticsOverview.productInSales"), value: catalog.in_sales ?? 0 },
    { key: "newProducts", title: i18n.t("statisticsProducts.newProducts"), value: created.new_products_count ?? 0 },
    { key: "editedProducts", title: i18n.t("statisticsProducts.editedProducts"), value: edited.edited_products_count ?? 0 },
    { key: "units", title: i18n.t("statisticsProducts.unitsSold"), value: sales.units_sold ?? 0 },
    buildMoneyCard("sales", i18n.t("statisticsProducts.salesAmount"), sales.sales_amount_fen),
    { key: "active", title: i18n.t("statisticsProducts.activeProducts"), value: sales.active_product_count ?? 0 },
    { key: "rate", title: i18n.t("statisticsProducts.activeRate"), value: sales.active_rate ?? 0, suffix: "%" },
  ];

  const compareItems = [
    { key: "new_products_count", label: i18n.t("statisticsProducts.newProducts"), data: comparison.new_products_count },
    { key: "edited_products_count", label: i18n.t("statisticsProducts.editedProducts"), data: comparison.edited_products_count },
    { key: "units_sold", label: i18n.t("statisticsProducts.unitsSold"), data: comparison.units_sold },
    { key: "sales_amount_fen", label: i18n.t("statisticsProducts.salesAmount"), data: comparison.sales_amount_fen, isMoney: true },
    { key: "active_product_count", label: i18n.t("statisticsProducts.activeProducts"), data: comparison.active_product_count },
  ];

  const topColumns = [
    { title: i18n.t("statisticsProducts.productName"), dataIndex: "zh_name" },
    { title: i18n.t("statisticsProducts.unitsSold"), dataIndex: "units_sold" },
    { title: i18n.t("statisticsProducts.salesAmount"), dataIndex: "sales_amount_fen", render: formatMoneyFen },
    { title: i18n.t("statisticsProducts.share"), dataIndex: "share_percent", render: (v) => `${v}%` },
    {
      title: i18n.t("statisticsCommon.action"),
      render: (_, record) => (
        <Link to={`/main/product/list?keyword=${encodeURIComponent(record.zh_name || "")}`}>
          {i18n.t("statisticsCommon.viewProducts")}
        </Link>
      ),
    },
  ];

  return (
    <div className="statistics-page">
      <div className="statistics-page__header">
        <h2>{i18n.t("statisticsProducts.title")}</h2>
        <Link to="/main/product/list">
          {i18n.t("statisticsCommon.viewProducts")}
        </Link>
      </div>

      <StatisticsDateFilter value={dateRange} onChange={setDateRange} loading={loading} />

      <Card className="statistics-page__section">
        <Select
          style={{ width: 180, marginBottom: 16 }}
          value={productStatus}
          onChange={setProductStatus}
          options={STATUS_OPTIONS.map((v) => ({ value: v, label: i18n.t(`statisticsProducts.statuses.${v}`) }))}
        />
        <StatCardGrid items={statCards} loading={loading} columns={3} />
      </Card>

      <PeriodCompare items={compareItems} />

      <Card title={i18n.t("statisticsProducts.salesTrend")} className="statistics-page__section">
        <TrendChart
          loading={loading}
          data={data?.trend}
          series={[
            { field: "units_sold", name: i18n.t("statisticsProducts.unitsSold"), color: "#1677ff" },
            { field: "sales_amount_fen", name: i18n.t("statisticsProducts.salesAmount"), color: "#fa8c16" },
          ]}
        />
      </Card>

      <Card title={i18n.t("statisticsProducts.newProductsTrend")} className="statistics-page__section">
        <TrendChart
          loading={loading}
          data={data?.created_trend}
          series={[
            { field: "new_products_count", name: i18n.t("statisticsProducts.newProducts"), color: "#722ed1" },
          ]}
        />
      </Card>

      <Card title={i18n.t("statisticsProducts.editedProductsTrend")} className="statistics-page__section">
        <TrendChart
          loading={loading}
          data={data?.edited_trend}
          series={[
            { field: "edited_products_count", name: i18n.t("statisticsProducts.editedProducts"), color: "#13c2c2" },
          ]}
        />
      </Card>

      <Card
        title={i18n.t("statisticsProducts.topProducts")}
        className="statistics-page__section"
        extra={
          <ExportCsvButton filename="top-products.csv" columns={topColumns.filter((c) => c.dataIndex)} rows={topProducts} />
        }
      >
        <Table rowKey="product_id" loading={loading} columns={topColumns} dataSource={topProducts} pagination={false} size="middle" />
      </Card>
    </div>
  );
}

export default connect((state) => ({ user: getUserInfo(state) }))(ProductStatistics);
